import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Edit, User, MapPin, Calendar, Users, Activity, Clock, TrendingUp, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [employerFilter, setEmployerFilter] = useState('all');

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    filterPatients();
  }, [searchTerm, patients, sortBy, employerFilter]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPatients(response.data);
      setFilteredPatients(response.data);
    } catch (error) {
      // console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to handle both old and new name formats
  const getFullName = (identifying_data) => {
    if (!identifying_data) return 'Unknown';
    const first = identifying_data.first_name || '';
    const middle = identifying_data.middle_name || '';
    const last = identifying_data.last_name || '';
    const newFormat = `${first} ${middle} ${last}`.trim();
    return newFormat || identifying_data.full_name || 'Unknown';
  };

  const getDisplayName = (identifying_data) => {
    if (!identifying_data) return 'Unknown';
    const first = identifying_data.first_name || '';
    const last = identifying_data.last_name || '';
    const newFormat = `${first} ${last}`.trim();
    return newFormat || identifying_data.full_name || 'Unknown';
  };

  const filterPatients = () => {
    let filtered = patients.filter(patient => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        getFullName(patient.identifying_data).toLowerCase().includes(searchLower) ||
        patient.id?.toLowerCase().includes(searchLower)
      );
      const matchesEmployer = employerFilter === 'all' ||
        (employerFilter === 'none' && !patient.employer_id) ||
        patient.employer_id === employerFilter;
      return matchesSearch && matchesEmployer;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return getDisplayName(a.identifying_data).localeCompare(getDisplayName(b.identifying_data));
        case 'age':
          return new Date(a.identifying_data.birthdate) - new Date(b.identifying_data.birthdate);
        case 'status':
          return a.membership_status.status.localeCompare(b.membership_status.status);
        case 'location':
          return (a.identifying_data.location || '').localeCompare(b.identifying_data.location || '');
        case 'lastUpdate':
          return new Date(b.updated_at) - new Date(a.updated_at);
        case 'employer':
          return (a.employer_name || 'zzz').localeCompare(b.employer_name || 'zzz');
        default:
          return 0;
      }
    });

    setFilteredPatients(filtered);
  };

  // Get unique employers from patient list
  const employerOptions = React.useMemo(() => {
    const map = new Map();
    patients.forEach(p => {
      if (p.employer_id && p.employer_name) {
        map.set(p.employer_id, p.employer_name);
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [patients]);

  const calculateAge = (birthdate) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const calculateMembershipLength = (startDate, expiryDate) => {
    const start = new Date(startDate);
    const expiry = new Date(expiryDate);
    
    const yearDiff = expiry.getFullYear() - start.getFullYear();
    const monthDiff = expiry.getMonth() - start.getMonth();
    const dayDiff = expiry.getDate() - start.getDate();
    
    let totalMonths = yearDiff * 12 + monthDiff;
    
    // Round up if there's any day difference
    if (dayDiff > 0) {
      totalMonths += 1;
    }
    
    return totalMonths;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${day}-${year}`;
  };

  const getMembershipBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="badge-active">Active</Badge>;
      case 'inactive':
        return <Badge className="badge-inactive">Inactive</Badge>;
      case 'expiring_soon':
        return <Badge className="badge-expiring">Expiring</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatLocation = (location) => {
    if (!location) return '';
    // Shorten NCR city names
    if (location === 'Quezon City') return 'Q.C.';
    // Remove "City" from other NCR cities
    return location.replace(' City', '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading patients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      {/* Left Wave Decoration */}
      <div className="absolute left-0 top-0 bottom-0 w-1/4 pointer-events-none opacity-30">
        <svg viewBox="0 0 400 1080" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M0 0C150 200 100 400 200 540C300 680 250 880 150 1080H0V0Z" fill="url(#leftGradient)" />
          <defs>
            <linearGradient id="leftGradient" x1="0" y1="0" x2="200" y2="1080" gradientUnits="userSpaceOnUse">
              <stop stopColor="#0EA5E9" />
              <stop offset="1" stopColor="#06B6D4" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Right Wave Decoration */}
      <div className="absolute right-0 top-0 bottom-0 w-1/3 pointer-events-none opacity-30">
        <svg viewBox="0 0 600 1080" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M600 0C450 150 500 350 350 540C200 730 300 900 450 1080H600V0Z" fill="url(#rightGradient)" />
          <defs>
            <linearGradient id="rightGradient" x1="600" y1="0" x2="300" y2="1080" gradientUnits="userSpaceOnUse">
              <stop stopColor="#0284C7" />
              <stop offset="1" stopColor="#0EA5E9" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8" data-testid="provider-dashboard-header">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">My Patients</h1>
          <p className="text-lg text-slate-600">Manage and view your patient records</p>
        </div>

        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                type="text"
                placeholder="Search by name, email, or ID..."
                data-testid="patient-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={employerFilter}
                onChange={(e) => setEmployerFilter(e.target.value)}
                data-testid="employer-filter-select"
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
              >
                <option value="all">All Employers</option>
                <option value="none">Self-enrolled (No Employer)</option>
                {employerOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
              >
                <option value="name">Sort by Name</option>
                <option value="age">Sort by Age</option>
                <option value="status">Sort by Status</option>
                <option value="location">Sort by Location</option>
                <option value="employer">Sort by Employer</option>
                <option value="lastUpdate">Sort by Last Update</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="patients-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="hidden md:table-cell text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">#</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-slate-500" />
                      <span>Name</span>
                    </div>
                  </th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-slate-500" />
                      <span>Location</span>
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-slate-500" />
                      <span>Age</span>
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Users size={14} className="text-slate-500" />
                      <span>Sex</span>
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Activity size={14} className="text-slate-500" />
                      <span>Status</span>
                    </div>
                  </th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-slate-500" />
                      <span>Employer</span>
                    </div>
                  </th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-slate-500" />
                      <span>Last Update</span>
                    </div>
                  </th>
                  <th className="hidden lg:table-cell text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={14} className="text-slate-500" />
                      <span>Length</span>
                    </div>
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-8 text-slate-500">
                      {employerFilter !== 'all'
                        ? 'No patients found for the selected employer'
                        : 'No patients found'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredPatients.map((patient, index) => (
                    <tr key={patient.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="hidden md:table-cell py-4 px-4 text-center text-slate-600 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          {patient.identifying_data.profile_photo ? (
                            <img
                              src={patient.identifying_data.profile_photo}
                              alt={getDisplayName(patient.identifying_data)}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                              <span className="text-xl font-bold text-cyan-700">
                                {getDisplayName(patient.identifying_data).charAt(0) || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{getDisplayName(patient.identifying_data)}</p>
                            <p className="text-xs text-slate-500 font-mono">ID: {patient.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden lg:table-cell py-4 px-4 text-slate-700 text-sm whitespace-nowrap">
                        {patient.identifying_data.location ? (
                          <div className="flex items-center gap-1.5">
                            <MapPin size={14} className="text-cyan-600" />
                            <span className="font-medium">{formatLocation(patient.identifying_data.location)}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">Not set</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-slate-700">{calculateAge(patient.identifying_data.birthdate)}</td>
                      <td className="py-4 px-4 text-slate-700">{patient.identifying_data.sex}</td>
                      <td className="py-4 px-4">{getMembershipBadge(patient.membership_status.status)}</td>
                      <td className="hidden lg:table-cell py-4 px-4 text-slate-700 text-sm whitespace-nowrap">
                        {patient.employer_name ? (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={14} className="text-teal-600" />
                            <span className="font-medium">{patient.employer_name}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">Self-enrolled</span>
                        )}
                      </td>
                      <td className="hidden lg:table-cell py-4 px-4 text-slate-700 text-sm whitespace-nowrap">
                        {formatDate(patient.updated_at)}
                      </td>
                      <td className="hidden lg:table-cell py-4 px-4 text-slate-700 text-sm font-medium whitespace-nowrap">
                        {calculateMembershipLength(patient.membership_status.start_date, patient.membership_status.expiry_date)} months
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-end">
                          <Button
                            onClick={() => navigate(`/provider/patient/${patient.id}/edit`)}
                            data-testid={`edit-patient-${patient.id}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                          >
                            <Edit size={14} className="mr-1" />
                            <span className="hidden sm:inline">Update Patient Record</span>
                            <span className="sm:hidden">Update</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderDashboard;